import { useCallback, useMemo } from "react";
import classNames from "classnames";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { historyViewConfig, HistoryRow } from "./views/historyViewConfig";
import { defaultHistoryView } from "./views/defaultHistoryView";
import { useHistoryDrawer } from "./historyDrawerContext";
import { isTinAccessRow } from "./utils/getActivityDescription";
import styles from "./HistoryPage.module.scss";

interface HistoryTableCallbacks {
    onViewDetails: (row: HistoryRow) => void;
}

export type HistoryTableMeta = ViewTableMeta<HistoryTableCallbacks, unknown>;

interface Props {
    data: HistoryRow[];
    footer?: React.ReactNode;
    /** The empty state's Clear filters action; omitted, it shows no button. */
    clearFilters?: () => void;
    className?: string;
}

export const History = ({ data, footer, clearFilters, className }: Props) => {
    const { openDrawer } = useHistoryDrawer();

    const handleView = useCallback(
        (row?: HistoryRow) => {
            if (!row || isTinAccessRow(row)) {
                return;
            }
            openDrawer(row);
        },
        [openDrawer],
    );

    const tableMeta: HistoryTableMeta = useMemo(
        () => ({
            callbacks: {
                onViewDetails: handleView,
            },
        }),
        [handleView],
    );

    return (
        <ViewTable
            data={data}
            view={defaultHistoryView}
            config={historyViewConfig}
            className={classNames(styles.historyView, className)}
            tableMeta={tableMeta}
            onRowClick={handleView}
            enableKeyboardShortcuts
            fixedTopOffsetPx
            emptyView={() => (
                <EmptyState
                    header="No activity available"
                    ctaText="Clear filters"
                    action={clearFilters}
                />
            )}
            footer={data.length > 0 ? footer : undefined}
        />
    );
};
